import { InfoOutlined } from "@mui/icons-material";
import { Button, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { AxiosError } from "axios";
import ArrowPopper from "components/app/ArrowPopper";
import MButton from "components/momentum/MButton";
import AccountSelectPop from "components/summary/index/ExpandPlanRevamp/SelectAccount/AccountSelectPop";
import { useSpContext } from "components/summary/success-plan";
import { SuccessPlanState } from "components/summary/success-plan/state/successPlan.types";
import { saveAs } from "file-saver";
import { random } from "lodash";
import {
  CURRENT_ACCOUNTKEY,
  FEATURE_KEY,
  FINAL_SELECTED_ACCOUNT,
  PRE_SELECTED_ACCOUNT,
  PRE_SELECTED_CONTACTS,
} from "constant";
import moment from "moment-timezone";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Account } from "types/portfolio";
import { SelectAccount } from "types/summary";
import { trackButton } from "utils";

import styles from "./SuccessPlan.module.scss";
import HtmlDialog from "components/dashboard/executive/components/HtmlDialog";

type SuccessPlanHeaderProps = {
  spState: SuccessPlanState;
  compact?: boolean;
  mutateEpRevamp?: () => void;
};

const SelectedAccountsRender = ({ accounts }: { accounts: Account[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const cnt = 4;
  const hasMore = accounts.length > cnt;
  const displayAccounts = hasMore ? accounts.slice(0, cnt) : accounts;

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Typography className={styles["sp-label-gray"]}>
        {displayAccounts.map((acc) => `${acc.accountName} (${acc.accountId})`).join(", ")}
      </Typography>
      {hasMore && (
        <Button
          onClick={handleClick}
          disableRipple
          disableTouchRipple
          disableFocusRipple
          sx={{
            textTransform: "none",
            ":hover": { backgroundColor: "transparent" },
            fontSize: "12px",
          }}
        >
          See More
        </Button>
      )}
      <ArrowPopper
        open={open}
        anchorEl={anchorEl}
        handleClose={handleClose}
        disableArrow={true}
      >
        <div>
          {accounts.slice(cnt).map((acc) => (
            <Typography
              key={`${acc.accountKey}-${acc.accountId}`}
              className={styles["sp-label"]}
            >{`${acc.accountName} (${acc.accountId})`}</Typography>
          ))}
        </div>
      </ArrowPopper>
    </>
  );
};

const SuccessPlanHeader = ({
  spState,
  compact = false,
  mutateEpRevamp,
}: SuccessPlanHeaderProps) => {
  const authDetail = useContext(AuthProvider);
  const { dispatch } = useSpContext();
  const axios = useAxios();

  const [openSelectAccounts, setOpenSelectAccounts] = useState(false);
  const [openSummarize, setOpenSummarize] = useState(false);
  const [summarize, setSummarize] = useState<string>("");

  //const [mode, setMode] = useState<"view" | "edit">("view");
  const ref = useRef<{ delete: (id: string) => void }>(null);
  const [planId, setPlanId] = useState<string>("");
  const { account, selectedAccounts, mode, spData } = spState;

  const selectedAccountKeys: string[] | undefined = useMemo(() => {
    return selectedAccounts?.map((acc) => acc.accountKey);
  }, [selectedAccounts]);

  const handleOpenSelectAccounts = () => {
    setOpenSelectAccounts(true);
  };

  const handleCloseSelectAccounts = () => {
    setOpenSelectAccounts(false);
  };

  const getRes = (data: SelectAccount[]) => {
    // data is correct
    const newData: any = [];
    data.map((i) => {
      newData.push({ accountKey: i.accountKey });
    });
    sessionStorage.setItem(FINAL_SELECTED_ACCOUNT, JSON.stringify(newData));
    if (authDetail?.features?.includes(FEATURE_KEY.EDIT_SUCCESS_PLAN)) {
      handleSectionSubmit("account")();
    }
  };

  const handleSectionSubmit = (type?: string) => async () => {
    dispatch({ type: "request/pending" });
    const selAccount = sessionStorage.getItem(FINAL_SELECTED_ACCOUNT);
    let accountSaved;
    if (selAccount) {
      accountSaved = JSON.parse(selAccount);
    }
    let postData: any = {
      accountKeys: accountSaved
        ? accountSaved?.map((i: any) => i.accountKey)
        : selectedAccounts?.map((s) => s.accountKey),
    };
    const { customerRisk } = spState.spData;

    if (type === "account") {
      if (authDetail?.features?.includes(FEATURE_KEY.EDIT_EXPAND_PLAN_REVAMP)) {
        if (!customerRisk) {
          toast.error("The customer risk field is required!", {
            position: "top-center",
            autoClose: false,
          });
          return;
        } else {
          postData = {
            ...postData,
            customerRisk,
          };
        }
      }
    }

    if (planId) {
      try {
        const { data: _data } = await axios.patch(
          `/api/dataservice/v1/success-plans/${planId}`,
          postData
        );

        await mutateEpRevamp?.();
        sessionStorage.removeItem(PRE_SELECTED_ACCOUNT);
        sessionStorage.removeItem(PRE_SELECTED_CONTACTS);
        sessionStorage.removeItem(FINAL_SELECTED_ACCOUNT);
        sessionStorage.removeItem(CURRENT_ACCOUNTKEY);
        // if (type === "submit") {
        toast.success("Success Plan updated", {
          position: "top-center",
          autoClose: 3000,
        });
        // }
      } catch (e) {
        // setUpdating(false);
        dispatch({ type: "request/failure" });
        toast.error(
          (e as AxiosError<{ message: string }>)?.response?.data?.message ||
            "Fail to update success plan",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
      }
    } else {
      try {
        const { data: epId } = await axios.post<string>(
          `/api/dataservice/v1/success-plans`,
          postData
        );
        setPlanId(epId);

        await mutateEpRevamp?.();
        // setUpdating(false);
        // onCancelEdit?.(fieldName?.[0]);
        sessionStorage.removeItem(PRE_SELECTED_ACCOUNT);
        sessionStorage.removeItem(PRE_SELECTED_CONTACTS);
        sessionStorage.removeItem(FINAL_SELECTED_ACCOUNT);
        sessionStorage.removeItem(CURRENT_ACCOUNTKEY);
        // if (type === "submit") {
        toast.success("Success Plan saved", {
          position: "top-center",
          autoClose: 3000,
        });
        // }
      } catch (e) {
        // setUpdating(false);
        dispatch({ type: "request/failure" });
        toast.error(
          (e as AxiosError<{ message: string }>)?.response?.data?.message ||
            "Fail to save success plan",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
      }
    }
  };

  const handleEdit = () => {
    dispatch({ type: "setMode", payload: "edit" });
  };

  useEffect(() => {
    if (spData) {
      setPlanId(spData.id);
    }
  }, [spData]);

  useEffect(() => {
    const date = account?.summarizedDate
      ? moment(account?.summarizedDate).tz("America/Los_Angeles").format("MMM Do, YYYY (PT)")
      : "N/A";

    let txt = `<p style="font-size: 12px;">Summarized at: <span style="color: #007AA3; font-size: 10px;">${date}</span></p><br/>`;
    const lis = (account?.summarizedData ?? "").split("\n").filter((x: string) => x.trim() !== "");
    txt += lis.reduce((cur: string, prv: string) => {
      cur += `<li>${prv.replace("- ", "")}</li>`;
      return cur;
    }, "<ul>");
    setSummarize(txt + "</ul>");
  }, [account?.summarizedData, account?.summarizedDate]);

  const handleSummarizeOpen = () => {
    setOpenSummarize(true);
  };

  const downloadHandler = (url: string, btnName: string) => () => {
    trackButton(btnName);
    const toastContent = () => (
      <div>
        <p>Please wait while we are fetching the data</p>
        <LinearProgress
          color="primary"
          style={{ borderRadius: 5 }}
        />
      </div>
    );
    const toastId = `progress${random(5, true)}`;
    toast.info(toastContent(), {
      toastId,
      closeOnClick: false,
      autoClose: false,
      closeButton: true,
      hideProgressBar: false,
    });
    axios
      .get(url, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          const progress = e.total ? e.loaded / e.total : undefined;
          const progressText = e.total
            ? `${((e.loaded * 100) / e.total).toFixed(0)}%`
            : "Downloading...";

          toast.update(toastId, {
            progress,
            render: (
              <div>
                <p>Please wait while we are fetching the data</p>
                <p>{progressText}</p>
              </div>
            ),
          });
        },
      })
      .then((res) => {
        saveAs(
          new Blob([res.data], {
            type: res.headers["content-type"],
          }),
          res.headers.filename
        );
      })
      .catch(() => {
        toast.error("Failed to download", {
          position: "top-center",
          autoClose: 3000,
        });
      })
      .finally(() => {
        toast.dismiss(toastId);
      });
  };

  if (!account) {
    return null;
  }

  const editable =
    authDetail?.features?.includes(FEATURE_KEY.EDIT_EXPAND_PLAN_REVAMP) ||
    authDetail?.features?.includes(FEATURE_KEY.UPDATE_CONCIERGE) ||
    authDetail?.features?.includes(FEATURE_KEY.EDIT_PARTNER_SUCCESS) ||
    authDetail?.features?.includes(FEATURE_KEY.EDIT_EPSM_PARTNER_SUCCESS) ||
    authDetail?.features?.includes(FEATURE_KEY.EDIT_VIDCAST_SUCCESS);

  return (
    <header className={styles["sp-header"]}>
      <div>
        <Typography className={styles["sp-label"]}>Cisco Collab Success Plan</Typography>
        <div className={styles["sp-flex-horizontal"]}>
          <Typography className={styles["sp-title"]}>{account.accountName}</Typography>
          <Typography className={styles["sp-title-gray"]}>({account.accountId})</Typography>
        </div>
      </div>

      {!compact && (
        <div className={styles["sp-flex-vertical"]}>
          <div className={styles["sp-horizontal-space"]}>
            <div>
              <Typography className={styles["sp-label-gray"]}>Related Accounts:</Typography>
              <SelectedAccountsRender accounts={selectedAccounts || []} />
            </div>
            <div>
              <div className={styles["sp-flex-horizontal"]}>
                <span className={styles["sp-label"]}>Select Related Accounts</span>
                <Tooltip title="Use this selection to associate a single Success Plan to multiple accounts. You can choose from a list of accounts related by CAV BU ID to the account you are editing. Once saved, the same Success Plan will appear for, and can be edited from, any of the selected accounts.">
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`${styles["sp-textfield-button"]} ${authDetail?.features?.includes(FEATURE_KEY.EDIT_EXPAND_PLAN_REVAMP) ? null : styles["sp-textfield-button__pe"]}`}
                onClick={handleOpenSelectAccounts}
              >
                {selectedAccountKeys ? `${selectedAccountKeys?.length} Account(s) Selected` : null}
              </div>
            </div>
          </div>
          <div className={styles["sp-header-card"]}>
            <div className={styles["sp-flex-horizontal"]}>
              <img
                src={"/detail/cisco-confidential.svg"}
                style={{ width: 48, height: 41 }}
                alt=""
              />

              <div>
                <Typography className={styles["sp-subtitle"]}>Latest Version (Current)</Typography>
                <div className={styles["sp-flex-horizontal"]}>
                  <Typography className={styles["sp-label-gray"]}>Last updated on:</Typography>
                  <Typography className={styles["sp-label"]}>
                    {spData?.lastModifiedDate
                      ? moment(spData?.lastModifiedDate)
                          .tz("America/Los_Angeles")
                          .format("YYYY-MM-DD HH:mm:ss PT")
                      : "No data submitted yet"}
                  </Typography>
                  <Typography className={styles["sp-label-gray"]}>Last updated by:</Typography>
                  <Typography className={styles["sp-label"]}>
                    {spData?.modifiedUser || "No created yet"}
                  </Typography>
                </div>
              </div>
            </div>

            <div className={styles["sp-header-actions"]}>
              {account?.summarizedData && (
                <>
                  <IconButton
                    style={{
                      backgroundColor: "#F7F7F7",
                      padding: 0,
                      marginRight: 4,
                    }}
                    onClick={handleSummarizeOpen}
                    title="AI Summary"
                    size="large"
                  >
                    <img
                      src="/activity/bridgeit.svg"
                      alt=""
                      style={{ width: 35, height: 35 }}
                    />
                  </IconButton>
                  <HtmlDialog
                    open={openSummarize}
                    onClose={() => setOpenSummarize(false)}
                    title="Customer Health Assessment Notes Summarization"
                    content={summarize}
                  />
                </>
              )}
              <div className={styles["sp-export-actions"]}>
                <Typography className={styles["sp-export-label"]}>Download</Typography>
                <IconButton
                  className={`${styles["sp-export-button"]} ${styles["sp-export-button-pdf"]}`}
                  onClick={downloadHandler(
                    `/api/csdigital-blob/api/sp/v1/expand-plan/${account.accountId}?downloadType=pdf&managementType=0`,
                    "Cisco Collab Success Plan_PDF"
                  )}
                  title="PDF version"
                  size="large"
                >
                  <i className={`cui-icon icon icon-file-pdf_16 ${styles["sp-export-icon"]}`}></i>
                </IconButton>
                <IconButton
                  className={`${styles["sp-export-button"]} ${styles["sp-export-button-excel"]}`}
                  onClick={downloadHandler(
                    `/api/csdigital-blob/api/sp/v1/expand-plan/${account.accountId}?downloadType=xlsx&managementType=0`,
                    "Cisco Collab Success Plan_EXCEL"
                  )}
                  title="Excel version"
                  size="large"
                >
                  <i className={`cui-icon icon icon-file-excel_16 ${styles["sp-export-icon"]}`}></i>
                </IconButton>
              </div>
              <MButton
                onClick={() =>
                  window.open(`/spng/summary/expand-plan-revamp?accountId=${account.accountId}`)
                }
              >
                Old Version
              </MButton>
              {mode === "view" && editable && (
                <MButton
                  buttonType="blue"
                  onClick={handleEdit}
                >
                  {!!planId ? "Edit Success Plan" : "Create Success Plan"}
                </MButton>
              )}
            </div>
          </div>
        </div>
      )}

      <AccountSelectPop
        open={openSelectAccounts}
        onClose={handleCloseSelectAccounts}
        getRes={getRes}
        preSelectedIds={selectedAccountKeys || []}
        currentAccountId={account.accountId}
        ref={ref}
        cavBuId={(selectedAccounts?.[0]?.cavBuId as any) ?? ""}
        ciscoCsmId={(selectedAccounts?.[0]?.ciscoCsmId as any) ?? ""}
        fixedAccountId={selectedAccounts?.[0]?.accountKey as any}
      />
    </header>
  );
};

export default SuccessPlanHeader;
